import React, {useEffect, useState, useRef} from 'react';
import {Form, Input, InputNumber, Select, Table, Tooltip} from 'antd';
import './customTable.scss';
import http from '../../services/httpService';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useForm} from 'antd/lib/form/Form';
import Toast from '../toast/Toast';
import {t} from 'react-switch-lang';
import {ReactComponent as PlusImg} from './img/plus.svg';
import {ReactComponent as EditImg} from './img/edit.svg';
import {ReactComponent as DeleteImg} from './img/delete.svg';
import {ReactComponent as DeactivateImg} from './img/deactivate.svg';
import {ReactComponent as TickImg} from './img/tick.svg';
import {ReactComponent as CancelImg} from './img/cancel.svg';
import {Spinner} from 'react-bootstrap';
import Swal from 'sweetalert2';
import PreventTransitionPrompt from '../preventTransitionprompt/PreventTransition';

/**
 *
 * @param {object} props
 * @param {string} props.api 'Endpoint for fetching table data'
 * @param {array} props.columns 'Columns structure'
 * @param {integer} [props.rows=20] 'Number of rows to fetch'
 * @param {object} props.methods 'Object containing onUpdate, onCreate, onDelete methods.'
 * @param {function} props.methods.onUpdate 'Handles onUpdate event. Must return response object.'
 * @param {function} props.methods.onCreate 'Handles onCreate event. Must return response object.'
 * @param {function} props.methods.onDelete 'Handles onDelete event. Must return response object.'
 * @param {param} [addButtonRef] 'Refernce to the add new row button. No onClick handler is needed on the button. Just ref.'
 */
const CustomTable = ({
  api,
  query,
  sort,
  queryParamName,
  columns,
  rows = 20,
  methods,
  addButtonRef,
  modalEdit = false,
  locale,
  actionsRedirect,
  actionsWidth,
  onClickRow,
  actionsOpenModal,
  updateTable,
  onColumnAdd,
  onColumnEdit,
  onColumnDelete,
  roles,
  deactivateButton = false,
  canEdit= true,
  hideDeleteAndDeactivation= false,
  ...rest
}) => {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = useForm();
  const newRowId = t('common.newEntry');
  const [validateTrigger, setValidateTrigger] = useState();
  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const {onUpdate, onCreate, onDelete, onDeactivate} = methods;

  const {Option} = Select;

  if (typeof query === 'object') {
    /*** If filters query ***/
    let queryString = '';
    query.forEach((v, k) => {
      queryString += `${k}=${v}&`;
    });
    query = queryString.substring(0, queryString.length - 1);
    sort = sort ? `sort=${sort}` : '';
  } else {
    query = query
      ? queryParamName
        ? `${queryParamName}=${query}`
        : `name.contains=${query}`
      : '';
    sort = sort ? `sort=${sort}` : '';
  }

  const fetchData = async () => {
    loadingRef.current = true;
    pageRef.current = 0;

    try {
      const {data} = await http.get(
        `${api}?page=0&size=${rows}&${query}&${sort}`
      );

      setItems(data);
      setHasMore(data.length === rows);
      pageRef.current = 1;
    } catch (err) {
    } finally {
      loadingRef.current = false;
    }
  };

  const fetchMoreData = async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    const nextPage = pageRef.current;
    try {
      const {data} = await http.get(
        `${api}?page=${nextPage}&size=${rows}&${query}&${sort}`
      );

      setItems((prev) => [...prev, ...data]);
      setHasMore(data.length === rows);

      pageRef.current = nextPage + 1;
    } finally {
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    if (!editingRecord) {
      fetchData();
    } else
      Toast.fire({
        title: t('message.cancelOrSave'),
        icon: 'warning',
      });
  }, [query]);

  const isEditing = (record) => record.id === editingRecord?.id;

  const renderInput = ({inputType, options, ...rest}) => {
    switch (inputType) {
      case 'text':
        return <Input {...rest.trim()} />;
      case 'number':
        return <InputNumber {...rest} />;
      case 'select':
        return (
          <Select dropdownMatchSelectWidth={false} {...rest}>
            {options.map((option) => {
              return (
                <Option value={option.value} key={option.value}>
                  {option.label}
                </Option>
              );
            })}
          </Select>
        );
      case 'selectMulti':
        return (
          <Select
            mode="multiple"
            allowClear
            showArrow
            showSearch={false}
            dropdownMatchSelectWidth={false}
            {...rest}
          >
            {options.map((option) => {
              return (
                <Option value={option.value} key={option.value}>
                  {option.label}
                </Option>
              );
            })}
          </Select>
        );
      case 'customSelect':
        return (
          <Select
            // mode="multiple"
            allowClear={false}
            showArrow
            showSearch={true}
            optionFilterProp="children"
            filterOption={`${(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}`}
            dropdownMatchSelectWidth={false}
            {...rest}
          >
            {options.map((option) => {
              return (
                <Option value={option.value} key={option.value}>
                  {option.label}
                </Option>
              );
            })}
          </Select>
        );
      default:
        return <Input {...rest} />;
    }
  };

  const editableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    rules,
    record,
    index,
    children,
    options,
    disabled,
    min,
    max,
    placeholder,
    ...rest
  }) => {
    return (
      <td {...rest}>
        {editing ? (
          <Form.Item name={dataIndex} rules={rules}>
            {renderInput({
              inputType,
              disabled,
              options,
              // onPressEnter: handleUpdate,
              min,
              max,
              placeholder,
            })}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.inputType,
        dataIndex: col.dataIndex,
        title: col.title,
        rules: col.rules,
        editing: isEditing(record),
        options: col.options,
        disabled: col.disabled,
        min: col.min,
        max: col.max,
        placeholder: col.placeholder,
      }),
    };
  });

  const handleAdd = () => {
    if (editingRecord) {
      return Toast.fire({
        title: t('message.cancelOrSave'),
        icon: 'warning',
      });
    }
    const newRow = {};
    for (let key of columns) {
      if (key.inputType === 'selectMulti') newRow[key.dataIndex] = [];
      else newRow[key.dataIndex] = null;
    }
    newRow.id = newRowId;
    form.setFieldsValue({...newRow});
    setItems([newRow, ...items]);
    setEditingRecord(newRow);
  };

  const handleEdit = (record) => {
    if (editingRecord) {
      return Toast.fire({
        title: t('message.cancelOrSave'),
        icon: 'warning',
      });
    }
    const formatedRecord = {...record};
    for (let key in formatedRecord) {
      if (Array.isArray(formatedRecord[key])) {
        formatedRecord[key] = formatedRecord[key].map((item) => item.id);
      }
    }
    form.setFieldsValue({...formatedRecord});
    setEditingRecord(formatedRecord);
  };

  const handleCancel = (record) => {
    if (record.id === newRowId)
      setItems((items) => {
        const filtered = items.filter((item) => item.id !== newRowId);
        return filtered;
      });
    setEditingRecord(null);
  };

  const handleUpdate = async () => {
    try {
      const data = await form.validateFields();
      const response = await onUpdate(data, editingRecord, form);
      if (response.status === 200) {
        const index = items.findIndex((item) => item.id === response.data.id);
        const updatedItems = [...items];
        updatedItems[index] = {...response.data};
        setItems(updatedItems);
        setEditingRecord('');
      } else if (response.status === 403) {
        Swal.fire({
          text: `${t('message.permissionData')}`,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (err) {}
  };

  const handleCreate = async () => {
    try {
      const data = await form.validateFields();
      data.id = null; // Removes false id
      const response = await onCreate(data, form);
      if (response.status >= 200 && response.status <= 299) {
        const index = items.findIndex((item) => item.id === newRowId);
        const updatedItems = [...items];
        updatedItems[index] = {...response.data};
        setItems(updatedItems);
        setEditingRecord('');
      } else if (response.status === 403) {
        Swal.fire({
          text: `${t('message.permissionData')}`,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (err) {}
  };

  const handleDelete = async (record) => {
    try {
      const response = await onDelete(record);
      if (response.status === 204) {
        const filteredItems = items.filter((item) => item.id !== record.id);
        setItems(filteredItems);
      } else if (response.status === 403) {
        Swal.fire({
          text: `${t('message.permissionData')}`,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (err) {}
  };
  const handleDeactivate = async (record) => {
    try {
      const response = await onDeactivate(record);
      if (response.status === 204) {
        const filteredItems = items.filter((item) => item.id !== record.id);
        setItems(filteredItems);
      } else if (response.status === 403) {
        Swal.fire({
          text: `${t('message.permissionData')}`,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (err) {}
  };

  mergedColumns.push({
    key: 'actions',
    ellipsis: true,
    align: 'center',
    className: 'actions',
    width: actionsWidth ? actionsWidth : 70,
    render: (record) => {
      if (actionsRedirect) {
        let userInCharge = false;
        let userResponsible = false;
        if (roles.userInCharge) {
          roles.departments.forEach((department) => {
            if (record.departmentId === department) {
              userInCharge = true;
            }
          });
        }
        if (roles.userResponsible) {
          roles.departments.forEach((department) => {
            if (record.departmentId === department) {
              userResponsible = true;
            }
          });
        }
        return (
          <>
            {roles.userSuperAdmin || userInCharge || userResponsible ? (
              <Tooltip title={t(actionsRedirect.damageTooltip)}>
                <button
                  type="button"
                  className="btn p-0 mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onColumnAdd(record.id);
                  }}
                >
                  <PlusImg />
                </button>
              </Tooltip>
            ) : null}
            {roles.userSuperAdmin || roles.userRiskSector ? (
              <Tooltip title={t(actionsRedirect.editTooltip)}>
                <button
                  type="button"
                  className="btn p-0 mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onColumnEdit(record.id);
                  }}
                >
                  <EditImg />
                </button>
              </Tooltip>
            ) : null}
            {roles.userSuperAdmin || roles.userRiskSector ? (
              <Tooltip title={t(actionsRedirect.deleteTooltip)}>
                <button
                  type="button"
                  className="btn p-0 mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(record);
                  }}
                >
                  <DeleteImg />
                </button>
              </Tooltip>
            ) : null}
          </>
        );
      } else if (actionsOpenModal) {
        if (
          roles.userSuperAdmin ||
          roles.userResponsible ||
          roles.userInCharge
        ) {
          return (
            <>
              <Tooltip title={t(actionsOpenModal.editTooltip)}>
                <button
                  type="button"
                  className="btn p-0 mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onColumnEdit(record.id);
                  }}
                >
                  <EditImg />
                </button>
              </Tooltip>
              <Tooltip title={t(actionsOpenModal.deleteTooltip)}>
                <button
                  type="button"
                  className="btn p-0 mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(record);
                  }}
                >
                  <DeleteImg />
                </button>
              </Tooltip>
            </>
          );
        }
      } else if (isEditing(record)) {
        return (
          <div className="actions">
            <Tooltip
              placement="top"
              color={'#007dc3'}
              title={t('common.cancel')}
            >
              <button
                type="button"
                className="btn p-0 mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel(record);
                }}
              >
                <CancelImg />
              </button>
            </Tooltip>
            <Tooltip
              placement="top"
              color={'#007dc3'}
              title={t('common.confirm')}
            >
              <button
                type="submit"
                className="btn p-0"
                onClick={() =>
                  record.id === newRowId
                    ? handleCreate(record)
                    : handleUpdate(record)
                }
              >
                <TickImg />
              </button>
            </Tooltip>
          </div>
        );
      } else
        return (
          <>{record?.active === true ?
            <Tooltip placement="top" color={'#007dc3'} title={t('common.edit')}>
              {canEdit && <button
                type="button"
                className="btn p-0 mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  if (modalEdit) return onUpdate(null, record);
                  handleEdit(record);
                }}
              >
                <EditImg />
              </button>}
            </Tooltip>
          : record?.active === undefined ?
              <Tooltip placement="top" color={'#007dc3'} title={t('common.edit')}>
              {canEdit &&  <button
                  type="button"
                  className="btn p-0 mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (modalEdit) return onUpdate(null, record);
                    handleEdit(record);
                  }}
                >
                  <EditImg />
                </button>}
              </Tooltip>
              : ''
          }
            {!hideDeleteAndDeactivation ? <>
            <Tooltip
              placement="top"
              color={'#007dc3'}
              title={t('common.delete')}
            >
              <button
                type="button"
                className="btn p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(record);
                }}
              >
                <DeleteImg />
              </button>
            </Tooltip>
            {' '}
            {(record?.riskCategoryActiveStatus !== undefined || record?.processActiveStatus !== undefined) ?
              deactivateButton && (record?.riskCategoryActiveStatus || record?.processActiveStatus) && <Tooltip
              placement="top"
              color={'#007dc3'}
              title={  record?.active
                ? t("buttons.deactivateBtn")
                : t("buttons.activateBtn")}
            >
              <button
                type="button"
                className="btn p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeactivate(record);
                }}
              >
                <DeactivateImg />
              </button>
            </Tooltip>
            : deactivateButton && <Tooltip
              placement="top"
              color={'#007dc3'}
              title={  record?.active
                ? t("buttons.deactivateBtn")
                : t("buttons.activateBtn")}
            >
              <button
                type="button"
                className="btn p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeactivate(record);
                }}
              >
                <DeactivateImg />
              </button>
            </Tooltip>}</> : null}
          </>
        );
    },
  });

  if (addButtonRef && addButtonRef.current)
    addButtonRef.current.onclick = handleAdd;

  const loader = (
    <div className="text-center mt-3">
      <Spinner variant="primary" animation="border" />
    </div>
  );

  useEffect(() => {
    setValidateTrigger('onFinish');
  }, [editingRecord]);

  return (
    <InfiniteScroll
      next={fetchMoreData}
      hasMore={hasMore}
      loader={loader}
      scrollableTarget="app-body"
      dataLength={items.length}
    >
      <PreventTransitionPrompt when={editingRecord} />
      <Form
        form={form}
        validateTrigger={validateTrigger}
        onFinishFailed={() => {
          setValidateTrigger('onChange');
          form.validateFields(Object.keys(editingRecord));
        }}
      >
        <Table
          components={{body: {cell: editableCell}}}
          columns={mergedColumns}
          dataSource={items}
          pagination={false}
          sticky={true}
          rowKey={(record) => record.id}
          locale={{emptyText: t('common.noData')}}
          onRow={(record) => ({
            onClick: () => (onClickRow ? onClickRow(record.id) : null),
          })}
          rowClassName={(record) =>
            record?.active === false ? 'table-row-inactive' : ''
          }
          {...rest}
        />
      </Form>
    </InfiniteScroll>
  );
};
export default CustomTable;
